import { API_BASE_URL } from "@/config";
import { validate_id } from "@/utils/validations";

export default {
  name: "MaritalInformation",
  props: {
    getSavedSectionResponses: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      // Form fields
      client_marital_status: "",
      client_marriage_type: "",
      client_marriage_date: "",
      spouse_first_name: "",
      spouse_last_name: "",
      spouse_preferred_name: "",
      spouse_id_type: "",
      spouse_id_number: "",
      spouse_consent: "",

      // For API integration
      questions: [], // Store fetched questions
      question_map: {}, // Maps api_form_field to question_id
      error_messages: {}, // Validation errors

      // Options for dropdowns
      marital_status_options: [
        { value: "SINGLE", label: "Single" },
        { value: "MARRIED", label: "Married" },
        { value: "DIVORCED", label: "Divorced" },
        { value: "WIDOWED", label: "Widowed" },
      ],
      marriage_type_options: [
        { value: "ANC WITH ACCRUAL", label: "ANC With Accrual" },
        { value: "ANC WITHOUT ACCRUAL", label: "ANC Without Accrual" },
        {
          value: "IN COMMUNITY OF PROPERTY",
          label: "In Community of Property",
        },
        { value: "TRIBAL LAW", label: "Tribal Law" },
        { value: "FOREIGN LAW", label: "Foreign Law" },
      ],
      spouse_id_type_options: [
        { value: "RSA ID", label: "RSA ID" },
        { value: "PASSPORT", label: "Passport" },
        { value: "OTHER ID", label: "Other ID" },
      ],
    };
  },
  computed: {
    workflow_id() {
      return this.$store.getters.get_workflow_id;
    },
  },
  methods: {
    get_questions() {
      const TOKEN = localStorage.getItem("token");
      const GET_QUESTIONS_URL = `${API_BASE_URL}/verify_api/financial_information_api/get_marital_questions/`;

      $.ajax({
        url: GET_QUESTIONS_URL,
        method: "GET",
        contentType: "application/json",
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        beforeSend: () => {
          Swal.fire({
            text: "Fetching questions...",
            allowEscapeKey: false,
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
          });
        },
        success: (response) => {
          Swal.close();
          this.questions = response.questions;

          // Map each question's api_form_field to its id
          this.questions.forEach((question) => {
            if (question.api_form_field) {
              this.question_map[question.api_form_field] = question.id;
            }
          });

          this.load_saved_responses();
        },
        error: () => {
          Swal.close();
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to fetch marital questions. Please try again later.",
            confirmButtonText: "OK",
          });
        },
      });
    },
    handle_marital_status_change() {
      // Reset marriage-specific fields if not married
      if (this.client_marital_status !== "MARRIED") {
        this.client_marriage_type = "";
        this.client_marriage_date = "";
        this.spouse_first_name = "";
        this.spouse_last_name = "";
        this.spouse_preferred_name = "";
        this.spouse_id_type = "";
        this.spouse_id_number = "";
        this.spouse_consent = "";
        this.clear_spouse_errors();
      }
    },

    handle_marriage_type_change() {
      // Clear spouse consent if marriage type changes
      this.spouse_consent = "";
      this.error_messages.spouse_consent = "";
    },

    clear_spouse_errors() {
      // Clear validation errors for spouse-related fields
      const spouse_fields = [
        "client_marriage_type",
        "client_marriage_date",
        "spouse_first_name",
        "spouse_last_name",
        "spouse_preferred_name",
        "spouse_id_type",
        "spouse_id_number",
        "spouse_consent",
      ];

      spouse_fields.forEach((field) => {
        this.error_messages[field] = "";
      });
    },

    validate_marital_form() {
      this.error_messages = {};

      // Always validate marital status - check for empty, null, undefined, or whitespace
      const marital_status_value = this.client_marital_status;
      if (
        !marital_status_value ||
        (typeof marital_status_value === "string" &&
          marital_status_value.trim() === "")
      ) {
        this.error_messages.client_marital_status =
          "Marital Status is required.";
        return false; // Return immediately if marital status is not selected
      }

      if (this.questions.length > 0) {
        // Validate each field based on question requirements
        this.questions.forEach((question) => {
          const field_name = question.api_form_field;
          if (!field_name) return; // Skip if no field mapping

          // For married-specific fields, only validate if marital status is "MARRIED"
          const is_spouse_field =
            field_name.includes("spouse") ||
            field_name === "client_marriage_type" ||
            field_name === "client_marriage_date";

          if (is_spouse_field && this.client_marital_status !== "MARRIED") {
            return; // Skip validation for spouse fields if not married
          }

          // Special case for spouse consent based on marriage type
          if (field_name === "spouse_consent") {
            const requires_consent = [
              "IN COMMUNITY OF PROPERTY",
              "TRIBAL LAW",
              "FOREIGN LAW",
            ].includes(this.client_marriage_type);
            if (requires_consent && question.required && !this[field_name]) {
              this.error_messages[
                field_name
              ] = `${question.question} is required.`;
            }
            return;
          }

          // Spouse ID validation
          if (
            field_name === "spouse_id_number" &&
            this.spouse_id_type === "RSA ID" &&
            this.spouse_id_number
          ) {
            if (!validate_id(this.spouse_id_number)) {
              this.error_messages.spouse_id_number =
                "Please enter a valid South African ID number.";
              return;
            }
          }

          // Check if field is required and empty (check for null, undefined, or empty string)
          const field_value = this[field_name];
          const is_empty =
            field_value === null ||
            field_value === undefined ||
            field_value === "" ||
            (typeof field_value === "string" && field_value.trim() === "");

          if (question.required && is_empty) {
            this.error_messages[
              field_name
            ] = `${question.question} is required.`;
          }
        });
      } else {
        // Fallback validation if questions aren't loaded
        if (this.client_marital_status === "MARRIED") {
          const required_fields = [
            "client_marriage_type",
            "client_marriage_date",
            "spouse_first_name",
            "spouse_last_name",
            "spouse_id_type",
            "spouse_id_number",
          ];

          // Add consent if applicable
          if (
            ["IN COMMUNITY OF PROPERTY", "TRIBAL LAW", "FOREIGN LAW"].includes(
              this.client_marriage_type
            )
          ) {
            required_fields.push("spouse_consent");
          }

          required_fields.forEach((field) => {
            const field_value = this[field];
            const is_empty =
              field_value === null ||
              field_value === undefined ||
              field_value === "" ||
              (typeof field_value === "string" && field_value.trim() === "");
            if (is_empty) {
              this.error_messages[field] = "This field is required.";
            }
          });

          // ID validation fallback
          if (
            this.spouse_id_type === "RSA ID" &&
            this.spouse_id_number &&
            !validate_id(this.spouse_id_number)
          ) {
            this.error_messages.spouse_id_number =
              "Please enter a valid South African ID number.";
          }
        }
      }

      return Object.keys(this.error_messages).length === 0;
    },
    submit_marital_info(section = null, customer_id = null) {
      // Handle case where section is a DOM event (from template click)
      if (
        section &&
        (section instanceof Event || section.type === "click" || section.target)
      ) {
        section = null;
      }

      // Skip mixin validation when editing from admin view (section is provided)
      // The admin view handles its own data binding and validation
      if (!section && !this.validate_marital_form()) {
        console.error("Validation errors:", this.error_messages);
        Swal.fire({
          icon: "warning",
          title: "Incomplete Form",
          text: "Please complete all required fields before continuing.",
          confirmButtonText: "OK",
        });
        return;
      }

      const TOKEN = localStorage.getItem("token");
      const SAVE_RESPONSES_URL = `${API_BASE_URL}/verify_api/financial_information_api/save_marital_information/`;

      let responses = [];

      if (section && Array.isArray(section.questions)) {
        responses = section.questions.map((q) => ({
          question_id: q.question_id,
          answer: q.response?.toString() || "",
        }));
      } else {
        // Customer onboarding flow
        // Process each field in our data model
        Object.keys(this.$data).forEach((field_name) => {
          // Skip non-form fields or empty values
          if (
            !this.question_map[field_name] ||
            field_name === "questions" ||
            field_name === "question_map" ||
            field_name === "error_messages"
          ) {
            return;
          }

          // If not married, only include the marital status field
          if (
            this.client_marital_status !== "MARRIED" &&
            (field_name.includes("spouse") ||
              field_name === "client_marriage_type" ||
              field_name === "client_marriage_date")
          ) {
            return;
          }

          // Add to responses array
          responses.push({
            question_id: this.question_map[field_name],
            answer: this[field_name] || "",
          });
        });
      }

      const FORM_DATA = {
        workflow_id: this.workflow_id,
        responses: responses,
      };

      if (customer_id) {
        FORM_DATA.customer_id = customer_id;
      }

      $.ajax({
        url: SAVE_RESPONSES_URL,
        method: "POST",
        contentType: "application/json",
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        data: JSON.stringify(FORM_DATA),
        beforeSend: () => {
          Swal.fire({
            text: "Saving marital information...",
            allowEscapeKey: false,
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
          });
        },
        success: () => {
          Swal.close();
          if (section && customer_id) {
            Swal.fire({
              icon: "success",
              title: "Success",
              text: "Marital information changed successfully!",
              timer: 2000,
              showConfirmButton: false,
            });
          } else {
            this.$emit("validity-change", true);
          }
        },
        error: (xhr) => {
          Swal.close();
          let errorMessage =
            "Failed to save responses. Please try again later.";
          if (xhr.responseJSON && xhr.responseJSON.error) {
            errorMessage = xhr.responseJSON.error;
          }
          Swal.fire({
            icon: "error",
            title: "Error",
            text: errorMessage,
            confirmButtonText: "OK",
          });
        },
      });
    },
    async load_saved_responses() {
      try {
        const responses = await this.getSavedSectionResponses(
          "Marital Information"
        );

        const idToField = {};
        Object.entries(this.question_map).forEach(([field, qid]) => {
          idToField[qid] = field;
        });

        if (Array.isArray(responses) && responses.length > 0) {
          responses.forEach((resp) => {
            const field = idToField[resp.question_id];
            if (field && Object.prototype.hasOwnProperty.call(this, field)) {
              this[field] = resp.answer;
            }
          });
        }
      } catch (error) {
        console.error("Error loading saved responses:", error);
      }
    },
  },
  mounted() {
    if (this.$store.getters.get_user_role?.toLowerCase() === "customer") {
      this.get_questions();
    }
  },
};
